/**
 * Panel de oportunidades: "qué puedo añadir y dónde".
 *
 * Lista priorizada de artículos con capacidad de material disponible para
 * producir esta semana (máx producible > 0, celda aún vacía). Cada fila
 * rellena su máximo con un clic, sin tener que expandir formatos a mano.
 */
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { fmtMiles } from '@/utils/planProduccion/engine'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

export interface Opportunity {
  aKey: string
  descripcion: string
  itemid: string
  formato: string
  weekIdx: number
  weekLabel: string
  max: number
}

interface Props {
  dark: boolean
  items: Opportunity[]
  onAdd: (aKey: string, weekIdx: number, max: number) => void
  onClose: () => void
}

export function OpportunitiesPanel({ dark, items, onAdd, onClose }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  const cardCls = dark
    ? 'rounded-xl border border-emerald-500/25 bg-slate-900'
    : 'rounded-xl border border-emerald-300 bg-white shadow-sm'
  const rowCls = dark
    ? 'border-white/8 hover:bg-white/[0.04]'
    : 'border-slate-100 hover:bg-emerald-50/50'

  return (
    <div className={cardCls}>
      <div className={clsx('flex items-center gap-2 px-4 py-2.5 border-b', dark ? 'border-white/8' : 'border-slate-100')}>
        <span className="text-[13px]">💡</span>
        <div className="flex-1 min-w-0">
          <div className={clsx('text-[12px] font-bold', dark ? 'text-slate-100' : 'text-slate-800')}>
            {t('planFormato.oportunidadesTitulo')}
          </div>
          <div className={clsx('text-[10px]', dark ? 'text-slate-500' : 'text-slate-400')}>
            {t('planFormato.oportunidadesSub', { count: items.length })}
          </div>
        </div>
        <button
          onClick={onClose}
          className={clsx('shrink-0 text-[11px] px-2 py-1 rounded-lg transition-colors', dark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')}
        >
          ✕
        </button>
      </div>

      {items.length === 0 ? (
        <div className={clsx('px-4 py-6 text-center text-[11px]', dark ? 'text-slate-500' : 'text-slate-400')}>
          {t('planFormato.oportunidadesVacio')}
        </div>
      ) : (
        <div className="max-h-[240px] overflow-auto">
          {items.map((o) => (
            <div
              key={o.aKey}
              className={clsx('flex items-center gap-3 px-4 py-1.5 border-b last:border-b-0 transition-colors', rowCls)}
            >
              <div className="min-w-0 flex-1 leading-tight">
                <div className={clsx('text-[11px] font-semibold truncate', dark ? 'text-slate-100' : 'text-slate-800')}>
                  {o.descripcion}
                </div>
                <div className={clsx('text-[9px] truncate', dark ? 'text-slate-500' : 'text-slate-400')}>
                  {fmtArt(o.itemid)} · {o.formato}
                </div>
              </div>
              <span className={clsx('shrink-0 text-[10px] font-bold rounded px-1.5 py-0.5', dark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700')}>
                {o.weekLabel}
              </span>
              <span className={clsx('shrink-0 text-[11px] font-black tabular-nums w-16 text-right', dark ? 'text-emerald-400' : 'text-emerald-600')}>
                {fmtMiles(o.max)}
              </span>
              <button
                onClick={() => onAdd(o.aKey, o.weekIdx, o.max)}
                className={clsx(
                  'shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-colors cursor-pointer',
                  dark
                    ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                )}
              >
                + {t('planFormato.anadir')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
